import { useCallback } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { AppConfig } from "~/utils/AppConfig";

export type LogLevel = "info" | "warn" | "error";

export const useLogEvent = () => {
    const logEvent = useCallback(
        (eventData: Record<string, unknown> & { level?: LogLevel }) => {
            if (!AppConfig.isProduction) {
                return;
            }

            // Observability must never be load-bearing. Almost every caller is
            // a catch block or a failure branch that still owes the customer a
            // working page, and several assign customer-facing state around
            // this call — so a throw here would not merely lose a log, it
            // would hijack the failure path that was trying to report one.
            //
            // `useGetInitialProducts` is the worst shape of it: a throw from
            // the failed-load branch lands in the catch below it, which reads
            // `err.message` and calls `setError` with it — replacing "Failed
            // to load products" with the SDK's internals on the customer's
            // screen — and a second throw from the catch's own report rejects
            // the task outright. `saveOrderSnapshot` is the most expensive:
            // it runs on the far side of a successful charge, where a throw
            // reaches four checkout success handlers with the money already
            // taken.
            //
            // Swallowed rather than re-reported, because reporting a logging
            // failure through the logger is the call that just failed. The
            // cost is that a broken SDK is invisible from here, which is the
            // right trade against a broken checkout.
            try {
                const { message, level = "info", ...context } = eventData;
                const msg = typeof message === "string" ? message : "log event";

                if (level === "error") {
                    datadogLogs.logger.error(msg, context);
                } else if (level === "warn") {
                    datadogLogs.logger.warn(msg, context);
                } else {
                    datadogLogs.logger.info(msg, context);
                }
            } catch {
                // Deliberately empty; see above.
            }
        },
        []
    );

    return logEvent;
};

/**
 * The bound logger returned by `useLogEvent`. Exported so non-hook helpers
 * (which receive `logEvent` as a parameter) can type it without each
 * re-deriving `ReturnType<typeof useLogEvent>`.
 *
 * **This never throws**, and that is part of the contract rather than an
 * accident of the implementation — see the guard in `useLogEvent`. Helpers
 * that take a `LogEvent` therefore do not need to wrap their reporting, and
 * `saveOrderSnapshot` / `reconcileCookieWithStorage` deliberately do not.
 */
export type LogEvent = ReturnType<typeof useLogEvent>;
